import { gql } from "generated-graphql";

/**
 * What a shipment mutation invalidates.
 *
 * Shipping changes container amounts and statuses, and drops the shipped volume
 * out of the accumulation area's rollup, so those reads have to refresh
 * alongside the shipment ones — the ship path is the only thing that recomputes
 * area rollups today. Attaching a manifest reaches the same container rows from
 * the other side, writing the back-reference to the regulated line that took
 * each container's waste.
 *
 * Lives here rather than on the detail page because the manifest card mutates
 * the same reads and importing it from the page would close a cycle.
 */
export const SHIPMENT_REFETCH_QUERIES = [
  "GetWasteShipment",
  "FacilityWasteShipments",
  "TenantWasteShipments",
  "GetWasteContainer",
  "FacilityWasteContainers",
  "TenantWasteContainers",
  "ShippableContainers",
  "FacilityAccumulationAreas",
  "FacilityAccumulationAreasTile",
  "GetAccumulationArea",
];

/**
 * Attaching or detaching a manifest also changes what the manifest side shows —
 * its linked containers, and whether it still offers itself as a candidate — and
 * the facility's discrepant count. Those reads are not touched by any other
 * shipment mutation, so they ride on top of the shared list rather than in it.
 */
export const MANIFEST_LINK_REFETCH_QUERIES = [
  ...SHIPMENT_REFETCH_QUERIES,
  "ProcessedManifests",
  "FacilityAccumulationSummary",
  "TenantAccumulationSummary",
];

/**
 * Attaching or detaching a bill of lading changes the facility's discrepant
 * count the same way a manifest link does. No `ProcessedManifests` here — a
 * bill of lading is never a candidate on that list, so nothing there changes.
 */
export const BILL_OF_LADING_LINK_REFETCH_QUERIES = [
  ...SHIPMENT_REFETCH_QUERIES,
  "FacilityAccumulationSummary",
];

/**
 * Shared row/detail shape for shipments. Lines (with container + stream) and
 * the grouped vendor view ride along on every read — the backend include
 * always loads them, so field resolvers never re-query.
 */
gql(`
  fragment WasteShipmentLineItemFields on WasteShipmentLine {
    id
    quantity
    unit
    canonicalKg
    federalWasteCodes
    stateWasteCodes
    dotDescription
    profileNumber
    removeFillEventId
    shipOutcome
    matchedWasteLineUnifiedId
    updatedAt
    container {
      id
      containerLabel
      containerType
      isPermanent
      isFlowThrough
      amount
      amountUnit
      capacity
      capacityUnit
      status
      archivedAt
      shippedDate
      accumulationArea {
        id
        name
        type
      }
    }
    wasteStream {
      id
      name
      classification
    }
  }
`);

gql(`
  fragment WasteShipmentFields on WasteShipment {
    id
    facilityId
    manifestRequestNumber
    status
    vendor {
      id
      name
    }
    transporterName
    destinationName
    destinationEpaId
    plannedShipDate
    scheduledPickupDate
    shippedDate
    requestedAt
    requestGeneratedAt
    requestExportUrl
    manifestRequestStale
    backfilledFromManifest
    manifestReceivedAt
    manifestSigned
    reconciliation {
      status
      linkedManifestTrackingNumber
    }
    issues {
      ...issue
    }
    shipPreview {
      archivedCount
      emptiedTankCount
      undecidableCount
      keepsBalance {
        containerLabel
        remaining
        unit
      }
    }
    lines {
      ...WasteShipmentLineItemFields
    }
    manifestRequestLines {
      wasteStream {
        id
        name
        classification
      }
      containerCount
      quantity
      unit
      federalWasteCodes
      stateWasteCodes
      dotDescription
      profileNumber
    }
    totalQuantityByUnit {
      unit
      total
    }
    createdAt
    updatedAt
  }
`);

export const TENANT_WASTE_SHIPMENTS_QUERY = gql(`
  query TenantWasteShipments(
    $tenantId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenant(id: $tenantId) {
      id
      wasteShipments(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...WasteShipmentFields
          facility {
            id
            name
          }
        }
        count
      }
    }
  }
`);

export const FACILITY_WASTE_SHIPMENTS_QUERY = gql(`
  query FacilityWasteShipments(
    $facilityId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facility(id: $facilityId) {
      id
      name
      wasteShipments(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...WasteShipmentFields
        }
        count
      }
    }
  }
`);

/**
 * The detail read. `fieldDiffs` is selected here rather than on the shared
 * fragment because it is the only surface that renders the per-field table —
 * list rows and badges need nothing but `reconciliation.status`, and the diff
 * is computed at read (a manifest fetch plus a compare per linked row), so
 * putting it on the fragment would charge every list page for it.
 */
export const GET_WASTE_SHIPMENT_QUERY = gql(`
  query GetWasteShipment($id: ID!) {
    wasteShipment(id: $id) {
      ...WasteShipmentFields
      # Detail-only for the same reason as the manifest below: the clock is
      # computed per container from the accumulation ruleset, so selecting it on
      # the shared fragment charges every list page for every line. The contents
      # grid shows how long each container has left on site, since the deadline
      # is what decides which drums go on a shipment in the first place.
      lines {
        container {
          clock {
            effectiveDeadlineDate
          }
        }
      }
      # Detail-only: the field resolver looks the manifest up per shipment, so
      # selecting it on the shared fragment would make it one query per list row.
      # Null while the pipeline has deleted and not yet re-upserted the manifest
      # row, which is why the tracking number is read from the snapshot below
      # rather than from here.
      manifest {
        id
        manifestTrackingNumber
        shippedDate
      }
      reconciliation {
        status
        linkedManifestTrackingNumber
        linkedShippingDocumentNumber
        linkedShippingDocumentId
        attachedDocumentKind
        attachedDocumentTotalPounds
        fieldDiffs {
          field
          wasteStreamId
          wasteStreamName
          discrepant
          severity
          requestValue
          documentValue
          unit
        }
      }
      facility {
        id
        name
        # The page header states which site this shipment left, in the terms a
        # reader identifies a site by: its own code, its EPA ID, and where it
        # is. Only on the detail query — the shipment lists identify a facility
        # by name in a column and have no room for the rest.
        customerFacilityId
        streetAddress1
        city
        state
        zip
        facilityAlternateIds {
          id
          type
          value
          hasNoId
        }
      }
    }
  }
`);

/**
 * The builder's container row: the shared container-list fields plus the open
 * shipments the container already sits on, so the builder can flag a
 * double-packaged container inline. Scoped to the builder (not on the shared
 * WasteContainerListItem fragment) so the containers grid doesn't pay for a
 * field it doesn't show.
 */
gql(`
  fragment ShippableContainerItem on WasteContainer {
    ...WasteContainerListItem
    openShipments {
      id
      manifestRequestNumber
      status
    }
  }
`);

/**
 * The builder's container pool: central-accumulation containers ready to
 * ship, deadline-sorted by the backend. Reuses the container list fragment
 * so the picker shows the same fields as the containers grid.
 */
export const SHIPPABLE_CONTAINERS_QUERY = gql(`
  query ShippableContainers($facilityId: ID!, $search: String, $sort: [SortModel!]) {
    facility(id: $facilityId) {
      id
      name
      shippableContainers(search: $search, sort: $sort) {
        items {
          ...ShippableContainerItem
        }
        count
      }
    }
  }
`);

export const VALIDATE_WASTE_SHIPMENT_INPUT = gql(`
  query ValidateWasteShipmentInput($input: CreateWasteShipmentInput!) {
    wasteShipmentInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const CREATE_WASTE_SHIPMENT_MUTATION = gql(`
  mutation CreateWasteShipment($input: CreateWasteShipmentInput!) {
    createWasteShipment(input: $input) {
      ...WasteShipmentFields
    }
  }
`);

/**
 * The detail page's Save. One call so the header, container and pickup changes
 * commit or fail together — sending them as three mutations could leave the
 * header saved while the user was told the save failed.
 */
export const SAVE_WASTE_SHIPMENT_MUTATION = gql(`
  mutation SaveWasteShipment($input: SaveWasteShipmentInput!) {
    saveWasteShipment(input: $input)
  }
`);

export const CANCEL_WASTE_SHIPMENT_MUTATION = gql(`
  mutation CancelWasteShipment($id: ID!) {
    cancelWasteShipment(id: $id)
  }
`);

export const DELETE_WASTE_SHIPMENT_MUTATION = gql(`
  mutation DeleteWasteShipment($id: ID!) {
    deleteWasteShipment(id: $id)
  }
`);

/**
 * Options feed for the vendor picker: the canonical vendor list narrowed to
 * vendors on the tenant's own ingested manifests — customers never see the
 * global staff-managed list.
 */
export const TENANT_WASTE_VENDOR_OPTIONS_QUERY = gql(`
  query TenantWasteVendorOptions($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      wasteVendorOptions {
        id
        name
        usedByTenant
      }
    }
  }
`);

export const VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT = gql(`
  query ValidateUpdateWasteShipmentInput($input: UpdateWasteShipmentInput!) {
    wasteShipmentUpdateInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const VALIDATE_UPDATE_SHIPMENT_LINES_INPUT = gql(`
  query ValidateUpdateShipmentLinesInput($input: UpdateShipmentLinesInput!) {
    updateShipmentLinesInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const MARK_SHIPMENT_SHIPPED_MUTATION = gql(`
  mutation MarkShipmentShipped($input: MarkShipmentShippedInput!) {
    markShipmentShipped(input: $input)
  }
`);

/**
 * Renders the request document and moves the shipment to REQUESTED. The
 * returned url is signed and short-lived, so it is opened right away rather
 * than stored — the shipment's own requestExportUrl is re-signed on each read.
 */
export const GENERATE_MANIFEST_REQUEST_MUTATION = gql(`
  mutation GenerateManifestRequest($shipmentId: ID!, $format: ManifestRequestFormat!) {
    generateManifestRequest(shipmentId: $shipmentId, format: $format) {
      url
      format
    }
  }
`);

export const EMAIL_MANIFEST_REQUEST_MUTATION = gql(`
  mutation EmailManifestRequest($shipmentId: ID!, $format: ManifestRequestFormat!, $recipients: [String!]!) {
    emailManifestRequest(shipmentId: $shipmentId, format: $format, recipients: $recipients)
  }
`);

/** For a request arranged outside Encamp: status only, no document. */
export const MARK_SHIPMENT_REQUESTED_MUTATION = gql(`
  mutation MarkShipmentRequested($shipmentId: ID!) {
    markShipmentRequested(shipmentId: $shipmentId)
  }
`);

export const VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT = gql(`
  query ValidateMarkShipmentShippedInput($input: MarkShipmentShippedInput!) {
    markShipmentShippedInputValidator(input: $input) {
      ...issue
    }
  }
`);

/**
 * Attach is also the signed-copy flow: re-attaching the manifest already
 * linked, with `signed: true`, keeps the original received date. So the dialog
 * and the signed toggle call the same mutation.
 */
export const ATTACH_SHIPMENT_MANIFEST_MUTATION = gql(`
  mutation AttachShipmentManifest($input: AttachShipmentManifestInput!) {
    attachShipmentManifest(input: $input)
  }
`);

export const DETACH_SHIPMENT_MANIFEST_MUTATION = gql(`
  mutation DetachShipmentManifest($shipmentId: ID!) {
    detachShipmentManifest(shipmentId: $shipmentId)
  }
`);

/**
 * Pre-flight for the picker, so a refusal reads as an explanation on the row
 * the user is about to choose rather than an error after submit. Two rules
 * reach here: the manifest is already attached to another shipment, and the
 * shipment's contents are frozen because a manifest is attached.
 */
export const VALIDATE_ATTACH_SHIPMENT_MANIFEST_INPUT = gql(`
  query ValidateAttachShipmentManifestInput($input: AttachShipmentManifestInput!) {
    attachShipmentManifestInputValidator(input: $input) {
      ...issue
    }
  }
`);

/**
 * The picker's manifest pool: ingested manifests at the shipment's facility,
 * newest first, narrowed by the user's text.
 *
 * This reuses the manifests grid's own omnisearch (`facilityId:` scoping, with
 * free text matching the tracking number) rather than a bespoke candidate
 * field. The ranked candidate list runs the other direction — a manifest
 * offering its plausible shipments, which is what the auto-match fallback on
 * Manifest Detail uses. Here the user already knows which manifest they are
 * holding, so search beats ranking.
 */
export const CANDIDATE_MANIFESTS_QUERY = gql(`
  query CandidateManifestsForShipment($search: String, $pageSize: Int, $sort: [SortModel!]) {
    processedManifests(search: $search, page: 0, pageSize: $pageSize, sort: $sort) {
      items {
        id
        manifestTrackingNumber
        shippedDate
        vendor
        desFacilityName
        wasteLineCount
      }
      count
    }
  }
`);

/**
 * Attach a bill of lading. No `signed` argument — a bill of lading has no
 * unsigned-draft-then-signed-copy flow the way a manifest does, so re-attach
 * is only ever "replace with this document" and never "mark this one signed".
 */
export const ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION = gql(`
  mutation AttachShipmentBillOfLading($input: AttachShipmentBillOfLadingInput!) {
    attachShipmentBillOfLading(input: $input)
  }
`);

export const DETACH_SHIPMENT_BILL_OF_LADING_MUTATION = gql(`
  mutation DetachShipmentBillOfLading($shipmentId: ID!) {
    detachShipmentBillOfLading(shipmentId: $shipmentId)
  }
`);

/**
 * Pre-flight for the bill-of-lading picker, mirroring
 * VALIDATE_ATTACH_SHIPMENT_MANIFEST_INPUT: a document already attached
 * elsewhere, or the shipment already carrying a manifest.
 */
export const VALIDATE_ATTACH_SHIPMENT_BILL_OF_LADING_INPUT = gql(`
  query ValidateAttachShipmentBillOfLadingInput($input: AttachShipmentBillOfLadingInput!) {
    attachShipmentBillOfLadingInputValidator(input: $input) {
      ...issue
    }
  }
`);

/**
 * The bill-of-lading picker's pool: the facility's BILL_OF_LADING and
 * OTHER shipping documents. `kinds` excludes MANIFEST at the API level rather
 * than through the keyed search string — a MANIFEST-kind document is refused
 * by `billOfLadingNotAttachableViolation` categorically, so the picker must
 * not offer one, and that has to hold even if the `kind:` search-term parser
 * changes later.
 *
 * Deliberately does not filter out documents already linked to another
 * shipment, and does not filter by facility beyond the `facilityId:` term:
 * the validator surfaces those refusals by name on the row the user picks,
 * the same way the manifest picker leaves that to
 * `attachShipmentManifestInputValidator` instead of hiding the row.
 */
export const CANDIDATE_SHIPPING_DOCUMENTS_QUERY = gql(`
  query CandidateShippingDocumentsForShipment(
    $tenantId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenant(id: $tenantId) {
      id
      wasteShippingDocuments(
        search: $search
        # Paged rather than pinned to 0, so the picker's autocomplete can load
        # further pages as the reader scrolls instead of telling them to narrow
        # the search.
        page: $page
        pageSize: $pageSize
        sort: $sort
        kinds: [BILL_OF_LADING, OTHER]
      ) {
        items {
          id
          kind
          documentNumber
          generatorName
          transporterName
          destinationName
          shipDate
          facility {
            id
            name
          }
          lineCount
        }
        count
      }
    }
  }
`);

/**
 * Facilities with at least one shippable container — the only facilities the
 * builder can act on, so its picker offers exactly these (bounded list, no
 * server-search needed).
 */
export const FACILITIES_WITH_SHIPPABLE_CONTAINERS_QUERY = gql(`
  query FacilitiesWithShippableContainers($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      facilitiesWithShippableContainers {
        id
        name
      }
    }
  }
`);
